import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  ExternalLink,
  Loader2,
  User,
  Mail,
  Lock,
  Phone,
  MapPin,
  CheckCircle2,
  Info,
} from 'lucide-react';

const EXTERNAL_DISCLAIMER =
  "External Application — you will complete this application on the employer's official website. Creating an INASW account or continuing to an external link does not confirm that an application has been submitted. Please follow all employer instructions and verify your submission confirmation.";

export const JobDetailExternalApplyDialog = ({
  open,
  onOpenChange,
  job,
  externalStep,
  setExternalStep,
  expressForm,
  setExpressForm,
  registering,
  handleExternalRegister,
  starting,
  handleExternalStart,
  onSignIn,
  onGoDashboard,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
              <ExternalLink className="w-4 h-4 text-blue-600" />
            </div>
            <DialogTitle className="font-serif text-xl">External Application</DialogTitle>
          </div>
          <DialogDescription>
            {externalStep === 1
              ? 'Create a free account to save this job and continue to the employer.'
              : externalStep === 2
              ? 'We saved this position to your dashboard. Finish on the employer site.'
              : 'All set — track your interest in your dashboard.'}
          </DialogDescription>
        </DialogHeader>

        {externalStep === 1 ? (
          /* Step 1: Minimal signup */
          <form onSubmit={handleExternalRegister} className="space-y-4 py-2">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <Info className="w-4 h-4 inline mr-2" />
              Applying to <strong>{job?.title}</strong> at {job?.company_name}
            </div>

            <div className="space-y-3">
              <div>
                <Label htmlFor="ext-name" className="text-sm font-medium">Full Name *</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input id="ext-name" type="text" placeholder="Jane Smith"
                    value={expressForm.full_name}
                    onChange={(e) => setExpressForm({ ...expressForm, full_name: e.target.value })}
                    className="pl-10" required data-testid="ext-name-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="ext-email" className="text-sm font-medium">Email *</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input id="ext-email" type="email" placeholder="jane@example.com"
                    value={expressForm.email}
                    onChange={(e) => setExpressForm({ ...expressForm, email: e.target.value })}
                    className="pl-10" required data-testid="ext-email-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="ext-password" className="text-sm font-medium">Password *</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input id="ext-password" type="password" placeholder="Create a password"
                    value={expressForm.password}
                    onChange={(e) => setExpressForm({ ...expressForm, password: e.target.value })}
                    className="pl-10" required data-testid="ext-password-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="ext-license" className="text-sm font-medium">License *</Label>
                  <Select value={expressForm.license_type}
                    onValueChange={(value) => setExpressForm({ ...expressForm, license_type: value })}>
                    <SelectTrigger className="mt-1" data-testid="ext-license-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LCSW">LCSW</SelectItem>
                      <SelectItem value="LMSW">LMSW</SelectItem>
                      <SelectItem value="LBSW">LBSW</SelectItem>
                      <SelectItem value="SWA">SWA</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="ext-state" className="text-sm font-medium">State (Optional)</Label>
                  <div className="relative mt-1">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input id="ext-state" type="text" placeholder="TX"
                      value={expressForm.location}
                      onChange={(e) => setExpressForm({ ...expressForm, location: e.target.value })}
                      className="pl-10" data-testid="ext-state-input" />
                  </div>
                </div>
              </div>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input type="tel" placeholder="Phone (optional)"
                  value={expressForm.phone}
                  onChange={(e) => setExpressForm({ ...expressForm, phone: e.target.value })}
                  className="pl-10" data-testid="ext-phone-input" />
              </div>
            </div>

            <p className="text-xs text-gray-500 leading-relaxed">{EXTERNAL_DISCLAIMER}</p>

            <DialogFooter className="pt-1">
              <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={registering}
                className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="ext-register-btn">
                {registering ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>) : (<>Continue<ExternalLink className="w-4 h-4 ml-2" /></>)}
              </Button>
            </DialogFooter>

            <p className="text-xs text-center text-gray-500">
              Already have an account?{' '}
              <button type="button" onClick={onSignIn} className="text-primary hover:underline">Sign in</button>
            </p>
          </form>
        ) : externalStep === 2 ? (
          /* Step 2: Confirm + redirect */
          <div className="space-y-4 py-2">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-emerald-800">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">Saved to your dashboard</span>
              </div>
              <p className="text-sm text-emerald-700 mt-1">
                Continue to the employer's official website to complete your application.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 leading-relaxed">
              {EXTERNAL_DISCLAIMER}
            </div>
            <DialogFooter>
              <Button onClick={handleExternalStart} disabled={starting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="ext-continue-btn">
                {starting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Redirecting...</>) : (<>Continue to Official Application<ExternalLink className="w-4 h-4 ml-2" /></>)}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          /* Step 3: Done */
          <div className="space-y-4 py-2">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <ExternalLink className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Continue on the employer's site</h3>
              <p className="text-gray-600 text-sm mb-2">
                We opened <strong>{job?.company_name}</strong>'s application page in a new tab and saved this job to your dashboard.
              </p>
              <p className="text-xs text-gray-500">
                If the tab didn't open, use the button below.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              {job?.external_url && (
                <a href={job.external_url} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="w-full" data-testid="ext-reopen-btn">
                    Open Employer Application
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              )}
              <Button onClick={onGoDashboard} className="w-full bg-primary hover:bg-primary-hover text-white" data-testid="ext-dashboard-btn">
                Track in My Dashboard
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
